export interface CloudFile {
  provider: 'google' | 'dropbox' | 'local'
  id: string
  name: string
  mimeType?: string
  size?: number
  url: string
  iconUrl?: string
  file?: File
  accessToken?: string
}

function waitForGapi(): Promise<void> {
  return new Promise((resolve) => {
    const check = () => {
      if (window.gapi?.load) {
        resolve()
      } else {
        setTimeout(check, 100)
      }
    }
    check()
  })
}

function getLocalFileIcon(file: File): string {
  switch (file.type) {
    case 'application/pdf':
      return 'https://drive-thirdparty.googleusercontent.com/16/type/application/pdf'

    case 'application/msword':
      return 'https://drive-thirdparty.googleusercontent.com/16/type/application/msword'

    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      return 'https://drive-thirdparty.googleusercontent.com/16/type/application/vnd.openxmlformats-officedocument.wordprocessingml.document'

    case 'text/plain':
    case 'text/rtf':
      return 'https://drive-thirdparty.googleusercontent.com/16/type/text/rtf'

    default:
      return 'https://drive-thirdparty.googleusercontent.com/16/type/application/file'
  }
}

export function useCloudPicker() {
  const config = useRuntimeConfig()

  const googleApiKey = config.public.googleApiKey
  const googleClientId = config.public.googleClientId
  const googleScopes = 'https://www.googleapis.com/auth/drive.readonly'

  let googleAccessToken: string

  /** ---------------- Google Drive ---------------- */
  const getGoogleCloudAccessToken = (): Promise<string> =>
    new Promise((resolve, reject) => {
      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: googleClientId,
        scope: googleScopes,
        callback: (resp) => {
          if (resp.error) reject(resp)
          else {
            googleAccessToken = resp.access_token
            resolve(googleAccessToken)
          }
        }
      })

      tokenClient.requestAccessToken({ prompt: 'consent' })
    })

  const loadGoogleCloudPicker = async () => {
    await waitForGapi()
    return new Promise<void>((resolve) => {
      window.gapi.load('picker', resolve)
    })
  }

  const openGooglePicker = async (accept = '*/*'): Promise<CloudFile[]> => {
    if (!googleAccessToken) await getGoogleCloudAccessToken()
    await loadGoogleCloudPicker()

    return new Promise((resolve) => {
      const picker = new window.google.picker.PickerBuilder()
        .addView(window.google.picker.ViewId.DOCS)
        .setOAuthToken(googleAccessToken!)
        .setDeveloperKey(googleApiKey)
        .setSelectableMimeTypes(accept)
        .setCallback((data: any) => {
          if (data.action === window.google.picker.Action.PICKED) {
            resolve(
              data.docs.map((doc: any) => ({
                provider: 'google',
                id: doc.id,
                name: doc.name,
                mimeType: doc.mimeType,
                url: doc.url,
                size: doc.sizeBytes ? Number(doc.sizeBytes) : undefined,
                iconUrl: doc.iconUrl,
                accessToken: googleAccessToken
              }))
            )
          }

          if (data.action === window.google.picker.Action.CANCEL) {
            resolve([])
          }
        })
        .build()

      picker.setVisible(true)
    })
  }

  const downloadGoogleDriveFile = async (file: CloudFile): Promise<File> => {
    const res = await fetch(`https://www.googleapis.com/drive/v3/files/${file.id}?alt=media`, {
      headers: {
        Authorization: `Bearer ${file.accessToken}`
      }
    })

    const blob = await res.blob()

    return new File([blob], file.name, {
      type: file.mimeType || blob.type
    })
  }

  /** ---------------- Dropbox ---------------- */
  const openDropboxPicker = async (accept: string[]): Promise<CloudFile[]> => {
    if (!window.Dropbox) throw new Error('Dropbox SDK not loaded')

    return new Promise((resolve) => {
      window.Dropbox.choose({
        multiselect: false,
        linkType: 'direct',
        extensions: accept,
        success: (files: any[]) => {
          resolve(
            files.map((f: any) => ({
              provider: 'dropbox',
              id: f.id,
              name: f.name,
              url: f.link,
              size: f.bytes,
              iconUrl: f.icon
            }))
          )
        },
        cancel: () => resolve([])
      })
    })
  }

  const downloadDropboxFile = async (file: CloudFile): Promise<File> => {
    const res = await fetch(`/api/greenhouse/download?url=${file.url}&name=${file.name}`)
    console.log('api url', res.url)
    const blob = await res.blob()
    return new File([blob], file.name, { type: blob.type || file.mimeType || 'application/octet-stream' })
  }

  /** ---------------- Local File ---------------- */
  const openLocalPicker = async (accept = '*/*'): Promise<CloudFile[]> => {
    return new Promise((resolve) => {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = accept
      input.multiple = false
      input.style.display = 'none'

      input.onchange = () => {
        if (!input.files?.length) {
          resolve([])
          return
        }

        const file = input.files[0]
        const url = URL.createObjectURL(file)

        resolve([
          {
            provider: 'local',
            id: crypto.randomUUID(),
            name: file.name,
            mimeType: file.type,
            size: file.size,
            iconUrl: getLocalFileIcon(file),
            url,
            file
          }
        ])
      }

      document.body.appendChild(input)
      input.click()
      document.body.removeChild(input)
    })
  }

  const openPicker = async (provider: 'google' | 'dropbox' | 'local'): Promise<CloudFile[]> => {
    let defaultMimeTypes = ['.pdf', '.doc', '.docx', '.txt', '.rtf']
    switch (provider) {
      case 'google':
        defaultMimeTypes = [
          'application/vnd.google-apps.document',
          'application/pdf',
          'application/text/plain',
          'application/text/rtf'
        ]
        return openGooglePicker(defaultMimeTypes.join(','))
      case 'dropbox':
        return openDropboxPicker(defaultMimeTypes)
      case 'local':
        return openLocalPicker(defaultMimeTypes.join(','))
    }

    return []
  }

  return { openPicker, downloadDropboxFile, downloadGoogleDriveFile }
}
